import { useState } from 'react';
import { Building2, Podcast, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { EditableGallery } from '@/components/admin/EditableGallery';
import { EditableText } from '@/components/admin/EditableText';
import { useAuth } from '@/contexts/AuthContext';
import { useEditMode } from '@/contexts/EditModeContext';
import type { Brokerage } from '@/contracts/brokerage';

interface BrokerageHeroSectionProps {
  brokerage: Brokerage;
  selectedImage: string;
  onImageSelect: (url: string) => void;
  onUpdateField?: (field: string, value: any) => void;
}

export function BrokerageHeroSection({ 
  brokerage, 
  selectedImage,
  onImageSelect,
  onUpdateField,
}: BrokerageHeroSectionProps) {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();
  
  // Get the main display image
  const mainImage = selectedImage || brokerage.image || brokerage.logo;
  
  // Extract YouTube video ID
  const getYoutubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };
  
  const isVideo = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be') || !!getYoutubeVideoId(url);
  };

  const renderMedia = () => {
    if (!mainImage) {
      return (
        <div className="w-full md:w-[548px] md:max-w-full aspect-video bg-muted rounded-lg flex items-center justify-center">
          <Building2 className="h-24 w-24 text-muted-foreground" />
        </div>
      );
    }
    
    if (isVideo(mainImage)) {
      const videoId = getYoutubeVideoId(mainImage);
      return (
        <div className="w-full md:w-[548px] md:max-w-full aspect-video">
          <iframe
            className="w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="Brokerage video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    
    return (
      <img
        src={mainImage}
        alt={brokerage.name}
        className="w-full md:w-[548px] md:max-w-full rounded-lg object-contain"
      />
    );
  };

  // Combine gallery with demo video if exists
  const allMedia: string[] = [
    brokerage.image || brokerage.logo || '',
    ...(brokerage.gallery || []),
    ...(brokerage.demo_video_url ? [brokerage.demo_video_url] : []),
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      {/* Main Showcase - Apple Music-style dynamic background */}
      <div className="relative rounded-2xl overflow-hidden min-h-[280px] md:min-h-[320px]">
        {/* Dynamic blurred background layer (hidden on mobile) */}
        {mainImage && (
          <>
            <div 
              className="hidden md:block absolute inset-0 scale-150 blur-3xl opacity-70"
              style={{ 
                backgroundImage: `url(${mainImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            
            {/* Secondary glow layer (hidden on mobile) */}
            <div 
              className="hidden md:block absolute inset-0 scale-125 blur-2xl opacity-40"
              style={{ 
                backgroundImage: `url(${mainImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </>
        )}
        
        {/* Dark gradient overlay (hidden on mobile) */}
        <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        
        {/* Vignette effect (hidden on mobile) */}
        <div className="hidden md:block absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.3)_100%)]" />
        
        {/* Simple mobile background */}
        <div className="md:hidden absolute inset-0 bg-muted/30" />
        
        {/* Main content */}
        <div className="relative p-4 md:p-6 flex items-center justify-center min-h-[280px] md:min-h-[320px]">
          <div className="relative">
            {/* Soft shadow for floating effect */}
            {mainImage && (
              <div 
                className="absolute inset-0 translate-y-4 blur-2xl opacity-50 rounded-xl"
                style={{ 
                  backgroundImage: `url(${mainImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
            )}
            {/* Main image with lifted styling */}
            <div className="relative rounded-xl overflow-hidden shadow-[0_25px_60px_-12px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
              {renderMedia()}
            </div>
          </div>
        </div>
      </div>

      {/* Media Thumbnails / Gallery - Editable */}
      {(allMedia.length > 1 || (isEditor && isEditMode)) && onUpdateField && (
        <EditableGallery
          images={brokerage.gallery || []}
          selectedImage={selectedImage}
          onImageSelect={onImageSelect}
          onSave={async (images) => { onUpdateField('gallery', images); }}
        />
      )}

      {/* Non-editable gallery fallback */}
      {allMedia.length > 1 && (!isEditor || !isEditMode) && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {allMedia.map((url, index) => {
            const isActive = url === selectedImage || (index === 0 && !selectedImage);
            const videoId = isVideo(url) ? getYoutubeVideoId(url) : null;
            
            return (
              <button
                key={index}
                onClick={() => onImageSelect(url)}
                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  isActive ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
                }`}
              >
                {videoId ? (
                  <>
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                      alt={`Video ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play className="h-4 w-4 text-white" />
                    </div>
                  </>
                ) : (
                  <img
                    src={url}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Demo Video Button */}
      {brokerage.demo_video_url && !allMedia.includes(brokerage.demo_video_url) && (
        <button
          onClick={() => setVideoModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Play className="h-4 w-4" />
          Watch Overview Video
        </button>
      )}

      {/* Podcast Player */}
      {(brokerage.podcast_audio_url || (isEditor && isEditMode)) && (
        <Card className="p-3 sm:p-4 md:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-2 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-600 flex items-center justify-center">
                <Podcast className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <div className="flex-1 space-y-2 md:space-y-3">
              <div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-foreground flex items-center gap-2 flex-wrap">
                  <EditableText
                    value={brokerage.podcast_title || `Circle's Take: ${brokerage.name}`}
                    onSave={async (value) => { onUpdateField?.('podcast_title', value); }}
                    as="span"
                    disabled={!onUpdateField}
                  />
                  <Badge variant="secondary" className="text-xs">Under 5 min</Badge>
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Perfect for your drive between showings—get the key insights now
                </p>
              </div>
              {brokerage.podcast_audio_url ? (
                <audio controls className="w-full h-8 sm:h-10" preload="metadata">
                  <source src={brokerage.podcast_audio_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              ) : isEditor && isEditMode && (
                <div className="text-sm text-muted-foreground italic">
                  No podcast audio URL set. Add one via database.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-4xl p-0">
          {brokerage.demo_video_url && (
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(brokerage.demo_video_url)}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
